import React, { FC, useContext, useState } from 'react'
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import DialogContext from '../Authentication/DialogContext'
import { hideDialog } from '../utils/DialogUtil'
import ErrorMessage, { closeErrorAlert } from '../utils/ErrorMessage'
import { ConfirmDialogProps } from './ConfirmDialog'
import DataTable from './DataTable'
import DraggableModalDialog from './DraggableModalDialog'

const AssignDialog: FC<{ assignDialogProps: AssignDialogProps }> = ({ assignDialogProps: props }) => {
  const { t } = useTranslation()
  const {
    show,
    backdrop = 'static',
    header = t('assignDialog'),
    columns = [],
    data = [],
    submitText = t('assign'),
    onSubmit,
    onSubmitExtraArgs = [],
    showCancelButton = true,
    cancelText = t('cancel'),
  } = props

  const [validated, setValidated] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [form, setFormValues] = useState(data)
  const { assignDialogPropsSetter, confirmDialogPropsSetter } = useContext(DialogContext)
  const [error, setError] = React.useState(null as any)
  const [showError, setShowError] = React.useState(false)
  const assignField = columns.find((column) => column.isAssignField)?.dataField

  if (columns.filter((column) => column.isAssignField).length !== 1 || !assignField) {
    return (
      <Modal
        dialogAs={DraggableModalDialog}
        show={show}
        onHide={() => hideDialog(assignDialogPropsSetter)}
        backdrop={backdrop}
        size='lg'
        aria-labelledby='contained-modal-title-vcenter'
        centered
        className='modal-dark'>
        <Modal.Header closeButton>
          <Modal.Title id='contained-modal-title-vcenter'>{header}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant='danger' className='my-auto'>
            {t('multipleAssignFieldError')}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => hideDialog(assignDialogPropsSetter)}>Ok</Button>
        </Modal.Footer>
      </Modal>
    )
  }

  const dataTableColumns = columns.map((column) => {
    const { dataField, text, isAssignField = false, formatter, sort = false } = column
    if (isAssignField) {
      return {
        text,
        dataField,
        formatter: (cellData: any, row: any) => {
          return (
            <Form.Check
              type='switch'
              label=''
              id={row.id}
              checked={form.find((formValue) => row.id === formValue.id)[assignField]}
              onChange={(e: any) => {
                const id = Number(e.target.id)
                const checked = e.target.checked
                setFormValues((formValues: any[]) =>
                  formValues.map((formValue) => {
                    if (id === formValue.id) {
                      formValue[assignField] = checked
                    }
                    return formValue
                  })
                )
              }}
            />
          )
        },
      }
    } else {
      return {
        text,
        dataField,
        sort,
        formatter,
      }
    }
  })

  const handleSubmit = (event: any) => {
    setSubmitting(true)
    const formTarget = event.currentTarget
    event.preventDefault()
    if (formTarget.checkValidity() === false) {
      event.stopPropagation()
    } else {
      onSubmit(form, ...onSubmitExtraArgs)
        .then((message) => {
          const confirmDialogProps: ConfirmDialogProps = {
            show: true,
            header: header,
            body: message,
            submitText: t('ok'),
            showCancelButton: false,
          }
          hideDialog(assignDialogPropsSetter)
          confirmDialogPropsSetter(confirmDialogProps)
        })
        .catch((error) => {
          setSubmitting(false)
          setError(error.message)
          setShowError(true)
        })
    }
    setSubmitting(false)
    setValidated(true)
  }

  return (
    <Modal
      dialogAs={DraggableModalDialog}
      show={show}
      onHide={() => hideDialog(assignDialogPropsSetter)}
      backdrop={backdrop}
      size='lg'
      aria-labelledby='contained-modal-title-vcenter'
      centered
      className='modal-dark'>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title id='contained-modal-title-vcenter'>{header}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DataTable data={data} columns={dataTableColumns} condensed={true} />
          <ErrorMessage error={error} show={showError} onClose={closeErrorAlert(setError, setShowError)} />
        </Modal.Body>
        <Modal.Footer>
          <Button type='submit' disabled={submitting} block={true} className='w-auto'>
            {submitting ? (
              <>
                <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' />
                <span className='ml-1'>{t('submitting')}</span>
              </>
            ) : (
              submitText
            )}
          </Button>
          {showCancelButton && (
            <Button variant='outline-danger' onClick={() => hideDialog(assignDialogPropsSetter)}>
              {cancelText}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export type AssignDialogProps = {
  show: boolean
  backdrop?: 'static' | true | false
  header?: string | JSX.Element
  columns: TableProps[]
  data: any[]
  onSubmit: (...args: any[]) => Promise<string>
  onSubmitExtraArgs?: any[]
  submitText?: string
  showCancelButton?: boolean
  cancelText?: string
}

export type TableProps = {
  dataField: string
  text: string
  sort?: boolean
  formatter?: any
  isAssignField?: boolean
}

export default AssignDialog
